import moment from 'moment';
import 'moment-timezone';
import { Availability, FromErpOverview } from './availability.types';
import { isClosedWeekday } from './PickTime.helpers';
import {
  CalendarDayState,
  CalendarDate,
  CalendarMonth,
  CalendarState,
  CalendarWeek,
  Calendar,
} from './PickTimeCalendar.types';

/**
 * Pure availability → calendar-data mapping for the calendar pick-time view
 * (MPA-2581, epic MPA-2598).
 *
 * Spec: marketplace-specs/specs/002-MPA-2598-weekpicker (reviewed 0e23647).
 * `_CVPickTimeCalendar` (Figma 502-22208) is a month-style calendar — month
 * sections of Monday-anchored week rows, day cells coloured by availability.
 * Selecting a day navigates to the week grid view for that week (legacy
 * behaviour); the month/calendar view shows no per-day time slots in the first
 * release. This is the decoupled "data/business" layer: it owns day
 * classification and campaign detection (ported from the legacy WeekPicker.js).
 * It does no fetching and no i18n — locale-dependent labels (month/weekday
 * names) are formatted in the view. `referenceTime` is injected so today/past
 * resolution is deterministic and testable.
 *
 * No-times handling (the legacy "next available times" CTA) is intentionally out
 * of scope for this view — it lives with the week view (MPA-2583).
 */

const DAY_MS = 86_400_000;
// Safety ceiling on the rendered span, aligned to the backend per-employee
// book-ahead cap (MAX_BOOK_AHEAD_WEEKS = 52 in backend/main/helpers/place.ts).
// This is a runaway-render guard, NOT the booking horizon — the real horizon
// (overview maxDateChecked + per-employee book-ahead) is resolved when month
// navigation is wired (MPA-2583).
const MAX_WEEKS = 52;

// Build a moment from a ms timestamp in the place timezone when one is provided,
// else the runtime-local tz. Legacy WeekPicker.js set the place tz globally via
// moment.tz.setDefault (WeekPicker.js:57); this keeps that behaviour local and
// pure. Day-keys and week ranges derived from ms MUST use the same tz basis as
// the parseZone-based slot day-keys below, or days near midnight can be
// misclassified as past/today when the viewer's tz differs from the place.
const momentInTz = (ms: number, tz?: string): moment.Moment => (tz ? moment.tz(ms, tz) : moment(ms));
const dayKeyFromMs = (ms: number, tz?: string): string => momentInTz(ms, tz).format('YYYY-MM-DD');
const dayKeyFromIso = (iso: string): string => moment.parseZone(iso).format('YYYY-MM-DD');
const slotMs = (iso: string): number => moment.parseZone(iso).valueOf();

export type BuildCalendarOptions = {
  /** "Now" (ms); resolves today and past days. */
  referenceTime: number;
  /**
   * IANA place timezone (e.g. "Europe/Stockholm") used to derive day-keys and
   * the week range from ms timestamps, so they line up with the slot day-keys
   * (which carry the place offset). Defaults to the runtime-local tz when omitted.
   */
  placeTimezone?: string;
  /** Campaign window start as unix SECONDS (legacy WeekPicker.js convention). */
  campaignStart?: number;
  /** Campaign window end as unix SECONDS. */
  campaignEnd?: number;
  /**
   * Overview to classify against, overriding `availability.fromErpOverview`, so a
   * response that carries no open days does not blank the grid. It does not make
   * the grid navigation-stable on its own: the ERP recomputes the overview from
   * each fetch's anchor week, so a later week's open days are a narrower set, not
   * a superset.
   */
  overview?: FromErpOverview;
  /**
   * Start (ms) of the fetched week — the only span slots can prove `fullyBooked`
   * for. The overview covers the whole horizon but slots only one week, so
   * outside this span the absence of slots is absence of knowledge,
   * and an open day stays selectable. Omitted, no day claims slot knowledge.
   */
  slotWeekStart?: number;
  /**
   * Weekdays (0 = Sunday) the place's weekly schedule opens on. A day missing
   * from the overview reads as closed only on a weekday outside it; omitted, no
   * such day reads as closed.
   */
  openWeekdays?: number[];
  /** Override the visible range start (ms); defaults to the checked horizon. */
  rangeStart?: number;
  /** Override the visible range end (ms); defaults to the checked horizon. */
  rangeEnd?: number;
};

export const buildCalendar = (availability: Availability | undefined, options: BuildCalendarOptions): Calendar => {
  const { referenceTime, campaignStart, campaignEnd, placeTimezone, slotWeekStart, openWeekdays } = options;

  // Days that still have at least one future slot — drives the `available` state.
  // The day-key uses the slot's own (place) offset via parseZone, matching the
  // tz basis the range/today keys are derived from.
  const availableDateKeys = new Set(
    Object.values(availability?.calendarTimeSlots ?? {})
      .filter((s) => slotMs(s.start) >= referenceTime)
      .map((s) => dayKeyFromIso(s.start)),
  );
  const overview = options.overview ?? availability?.fromErpOverview;
  const openDays = new Set((overview?.datesWithOpeningHours ?? []).map(dayKeyFromIso));
  const todayKey = dayKeyFromMs(referenceTime, placeTimezone);
  const slotWeekFromKey = slotWeekStart ? dayKeyFromMs(slotWeekStart, placeTimezone) : undefined;
  const slotWeekToKey = slotWeekStart ? dayKeyFromMs(slotWeekStart + 6 * DAY_MS, placeTimezone) : undefined;

  const buildDay = (d: moment.Moment): CalendarDate => {
    const dateKey = d.format('YYYY-MM-DD');
    const isPast = dateKey < todayKey;
    const hasSlots = availableDateKeys.has(dateKey);
    // campaign.startDate/endDate are UTC-midnight unix seconds (legacy WeekPicker
    // compares against startOfDay() = moment.utc().startOf('day')), so reinterpret
    // the calendar date as UTC midnight rather than the local-tz day boundary.
    const daySeconds = moment.utc(dateKey, 'YYYY-MM-DD').unix();
    const hasCampaign = !!campaignStart && !!campaignEnd && daySeconds >= campaignStart && daySeconds <= campaignEnd;

    const isOpen = openDays.has(dateKey);
    const hasSlotKnowledge =
      !!slotWeekFromKey && !!slotWeekToKey && dateKey >= slotWeekFromKey && dateKey <= slotWeekToKey;

    let state: CalendarDayState;
    if (isPast) state = 'past';
    else if (hasSlots) state = hasCampaign ? 'campaign' : 'available';
    else if (!isOpen) state = isClosedWeekday(dateKey, openWeekdays) ? 'closed' : 'fullyBooked';
    else if (hasSlotKnowledge) state = 'fullyBooked';
    else state = hasCampaign ? 'campaign' : 'available';

    return {
      date: dateKey,
      dayNumber: d.date(),
      state,
      isToday: dateKey === todayKey,
      selectable: !isPast && (hasSlots || (isOpen && !hasSlotKnowledge)),
    };
  };

  // Resolve the visible range from the checked horizon, then snap to whole ISO weeks.
  const startMs = options.rangeStart ?? overview?.minDateChecked ?? referenceTime;
  const fallbackEnd = overview?.firstAvailableDate
    ? moment.parseZone(overview.firstAvailableDate).valueOf() + 7 * DAY_MS
    : referenceTime + 42 * DAY_MS;
  const endMs = options.rangeEnd ?? overview?.maxDateChecked ?? fallbackEnd;

  const rangeStartDay = momentInTz(startMs, placeTimezone).startOf('isoWeek');
  let rangeEndDay = momentInTz(endMs, placeTimezone).endOf('isoWeek');
  if (rangeEndDay.isBefore(rangeStartDay)) {
    rangeEndDay = rangeStartDay.clone().add(5, 'weeks').endOf('isoWeek');
  }
  if (rangeEndDay.diff(rangeStartDay, 'weeks') > MAX_WEEKS) {
    rangeEndDay = rangeStartDay.clone().add(MAX_WEEKS, 'weeks').endOf('isoWeek');
  }

  const months: CalendarMonth[] = [];
  const monthCursor = rangeStartDay.clone().startOf('month');
  const lastMonth = rangeEndDay.clone().startOf('month');
  while (monthCursor.isSameOrBefore(lastMonth, 'month')) {
    const year = monthCursor.year();
    const month = monthCursor.month();
    const weeksStart = moment.max(monthCursor.clone().startOf('month').startOf('isoWeek'), rangeStartDay.clone());
    const weeksEnd = moment.min(monthCursor.clone().endOf('month').endOf('isoWeek'), rangeEndDay.clone());

    const weeks: CalendarWeek[] = [];
    const weekCursor = weeksStart.clone().startOf('isoWeek');
    while (weekCursor.isSameOrBefore(weeksEnd, 'day')) {
      const days: (CalendarDate | null)[] = [];
      for (let i = 0; i < 7; i++) {
        const d = weekCursor.clone().add(i, 'days');
        const inMonth = d.month() === month && d.year() === year;
        const inRange = d.isSameOrAfter(rangeStartDay, 'day') && d.isSameOrBefore(rangeEndDay, 'day');
        days.push(inMonth && inRange ? buildDay(d) : null);
      }
      weeks.push({ weekNumber: weekCursor.isoWeek(), days });
      weekCursor.add(1, 'week');
    }
    if (weeks.length) months.push({ year, month, weeks });
    monthCursor.add(1, 'month');
  }

  const state: CalendarState = availableDateKeys.size ? 'loaded' : 'empty';

  return { months, state };
};
